import { createContext, useContext, useEffect, useMemo, useRef } from 'react';
import { getAuthSession } from 'auth/session';
import { useFlags } from 'launchdarkly-react-client-sdk';
import Intercom, { hide, show, shutdown } from '@intercom/messenger-js-sdk';
import { useGetApiV5IntercomSettings } from '@liscio/api';
import {
  IntercomBootFailureReporter,
  reportIntercomSettingsFetchFailure,
  stopIntercomBootWatch,
  watchIntercomBoot,
} from '@liscio/common';
import { useLocation } from 'react-router-dom-latest';
import deleteIntercomCookies from './utils/delete-intercom-cookies';

export type IntercomContextType = {
  hideIntercom: () => void;
  showIntercom: () => void;
  checkIfIntercomIsAvailable: () => boolean;
};

const DEFAULT_CONTEXT: IntercomContextType = {
  hideIntercom: () => {},
  showIntercom: () => {},
  checkIfIntercomIsAvailable: () => false,
};

export const IntercomContext =
  createContext<IntercomContextType>(DEFAULT_CONTEXT);

export type IntercomProviderProps = {
  children?: React.ReactNode;
  /**
   * Receives at most one bounded boot-failure outcome per page session.
   */
  onBootFailure?: IntercomBootFailureReporter;
};

const noopBootFailureReporter: IntercomBootFailureReporter = () => {};

/**
 * Intercom instance provider
 */
export const IntercomProvider = ({
  children,
  onBootFailure,
}: IntercomProviderProps) => {
  const { isIntercomEnabled } = useFlags();
  const location = useLocation();
  const authSession = getAuthSession();
  const isAuthenticated = !!authSession;

  // useGetApiV5IntercomSettings causes this provider to be re-rendered after logging in - good for re-initializing Intercom
  const {
    data: intercomSettings,
    isError: isIntercomSettingsError,
    error: intercomSettingsError,
  } = useGetApiV5IntercomSettings(
    {
      workspace: 'firm',
    },
    { query: { enabled: isAuthenticated } },
  );
  const reportBootFailureRef = useRef(onBootFailure);
  reportBootFailureRef.current = onBootFailure;

  /**
   * Initialize Intercom
   */
  useEffect(() => {
    if (isAuthenticated && intercomSettings && isIntercomEnabled) {
      if (intercomSettings) {
        Intercom({
          app_id: intercomSettings.app_id,
          hide_default_launcher: true,
          created_at: intercomSettings.created_at,
          intercom_user_jwt: intercomSettings.intercom_user_jwt,
          version: intercomSettings.version,
          //@ts-expect-error Intercom types are not correct
          companies: intercomSettings.companies.map((company) => ({
            company_id: company.company_id,
            created_at: company.created_at,
            name: company.name,
            size: company.size,
            is_user_default_message_recipient:
              company.is_user_default_message_recipient,
            edoc_requests_in_last_60_days:
              company.edoc_requests_in_last_60_days,
            sms_enabled_user_count: company.sms_enabled_user_count,
            email_active: company.email_active,
            email_active_user_count: company.email_active_user_count,
            standard_requests_count: company.standard_requests_count,
            standard_request_last_created_at:
              company.standard_request_last_created_at,
            organizer_sent_count: company.organizer_sent_count,
            organizer_last_sent_at: company.organizer_last_sent_at,
            smart_delivery_sent_count: company.smart_delivery_sent_count,
            smart_delivery_last_sent_at: company.smart_delivery_last_sent_at,
          })),
        });
        watchIntercomBoot({
          app: 'legacy',
          workspace: 'firm',
          report: (failure) =>
            (reportBootFailureRef.current ?? noopBootFailureReporter)(failure),
        });
      }
    }
  }, [intercomSettings, isAuthenticated, isIntercomEnabled]);

  /**
   * Report a failed Intercom settings request as the session's boot outcome
   */
  useEffect(() => {
    if (isAuthenticated && isIntercomSettingsError && isIntercomEnabled) {
      reportIntercomSettingsFetchFailure({
        app: 'legacy',
        workspace: 'firm',
        error: intercomSettingsError,
        report: (failure) =>
          (reportBootFailureRef.current ?? noopBootFailureReporter)(failure),
      });
    }
  }, [
    intercomSettingsError,
    isAuthenticated,
    isIntercomEnabled,
    isIntercomSettingsError,
  ]);

  /**
   * Stop a pending boot watch when the provider unmounts
   */
  useEffect(() => stopIntercomBootWatch, []);

  /**
   * Shutdown Intercom when user logs out
   */
  useEffect(() => {
    if (isIntercomEnabled && !isAuthenticated) {
      stopIntercomBootWatch();
      if (window.Intercom) {
        shutdown();
      }
      deleteIntercomCookies();
    }
  }, [location, isIntercomEnabled, isAuthenticated]);

  const ctx = useMemo(
    () => ({
      hideIntercom: hide,
      showIntercom: show,
      checkIfIntercomIsAvailable: () => Boolean(window.Intercom),
    }),
    [],
  );

  return (
    <IntercomContext.Provider value={ctx}>{children}</IntercomContext.Provider>
  );
};

export const useIntercom = () => {
  const context = useContext(IntercomContext);
  return context;
};
